/**
 * Basemap styles. Vector styles come from OpenFreeMap (keyless); the 'satellite' id is USGS National Map orthoimagery (US only, public domain; NAIP).
 * OFFLINE_STYLE needs no network at all (plain ground + graticule) so overlays always render.
 */
import type { Map as MlMap, StyleSpecification } from 'maplibre-gl';
import type { BasemapId } from '../../lib/types';

export const STYLE_URLS: Record<Exclude<BasemapId, 'satellite'>, string> = {
  dark: 'https://tiles.openfreemap.org/styles/dark',
  daylight: 'https://tiles.openfreemap.org/styles/positron',
};

export const SATELLITE_STYLE: StyleSpecification = {
  version: 8,
  sources: {
    usgs: {
      type: 'raster',
      tiles: ['https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer/tile/{z}/{y}/{x}'],
      tileSize: 256,
      maxzoom: 16,
      // USGSImageryOnly/MapServer copyrightText, read from the service metadata 2026-10-24. US coverage only;
      // outside the United States only low-resolution global imagery exists.
      attribution: 'USDA, USGS The National Map: Orthoimagery (US only)',
    },
  },
  layers: [
    { id: 'bg', type: 'background', paint: { 'background-color': '#0b0c0e' } },
    {
      id: 'usgs',
      type: 'raster',
      source: 'usgs',
      paint: { 'raster-saturation': -0.25, 'raster-brightness-max': 0.82, 'raster-contrast': 0.05, 'raster-fade-duration': 150 },
    },
  ],
};

export function offlineStyle(light: boolean): StyleSpecification {
  const lines: GeoJSON.Feature[] = [];
  // 0.1° graticule over the western US + a coarse global one
  for (let lon = -180; lon <= 180; lon += 1) lines.push(line([[lon, -85], [lon, 85]], lon % 10 === 0 ? 2 : 1));
  for (let lat = -80; lat <= 80; lat += 1) lines.push(line([[-180, lat], [180, lat]], lat % 10 === 0 ? 2 : 1));
  for (let lon = -125; lon <= -114; lon += 0.1) lines.push(line([[lon, 32], [lon, 42.5]], 0));
  for (let lat = 32; lat <= 42.5; lat += 0.1) lines.push(line([[-125, lat], [-114, lat]], 0));
  const ink = light ? '0,0,0' : '255,255,255';
  return {
    version: 8,
    sources: { grid: { type: 'geojson', data: { type: 'FeatureCollection', features: lines } } },
    layers: [
      { id: 'bg', type: 'background', paint: { 'background-color': light ? '#f3f3f1' : '#0c0d0f' } },
      {
        id: 'grid-fine',
        type: 'line',
        source: 'grid',
        minzoom: 9,
        filter: ['==', ['get', 'w'], 0],
        paint: { 'line-color': `rgba(${ink},0.05)`, 'line-width': 1 },
      },
      {
        id: 'grid',
        type: 'line',
        source: 'grid',
        filter: ['>', ['get', 'w'], 0],
        paint: { 'line-color': ['case', ['==', ['get', 'w'], 2], `rgba(${ink},0.14)`, `rgba(${ink},0.08)`], 'line-width': 1 },
      },
    ],
  };
}

function line(coords: [number, number][], w: number): GeoJSON.Feature {
  return { type: 'Feature', properties: { w }, geometry: { type: 'LineString', coordinates: coords } };
}

export function styleFor(b: BasemapId): string | StyleSpecification {
  if (b === 'satellite') return SATELLITE_STYLE;
  return STYLE_URLS[b];
}

/** Mute the OpenFreeMap dark style further so overlays own the colour. */
export function restyleDark(map: MlMap): void {
  const style = map.getStyle();
  for (const layer of style.layers ?? []) {
    const id = layer.id;
    try {
      if (layer.type === 'background') {
        map.setPaintProperty(id, 'background-color', '#0b0c0e');
      } else if (layer.type === 'fill' && /water|ocean|lake|river/i.test(id)) {
        map.setPaintProperty(id, 'fill-color', '#111519');
      } else if (layer.type === 'fill' && /park|landcover|landuse|wood|grass/i.test(id)) {
        map.setPaintProperty(id, 'fill-opacity', 0.35);
      } else if (layer.type === 'symbol') {
        if (/poi|shield/i.test(id)) {
          map.setLayoutProperty(id, 'visibility', 'none');
          continue;
        }
        map.setPaintProperty(id, 'text-color', /place|city|town|country|state/i.test(id) ? '#8d939a' : '#6a7078');
        map.setPaintProperty(id, 'text-halo-color', '#0b0c0e');
        map.setPaintProperty(id, 'text-halo-width', 1.2);
      } else if (layer.type === 'line' && /boundary|admin/i.test(id)) {
        map.setPaintProperty(id, 'line-opacity', 0.35);
      }
    } catch {
      /* unknown paint property for this layer type — skip */
    }
  }
}

/** Positron is already quiet; hide POIs and sharpen labels for direct-sun legibility. */
export function restyleDaylight(map: MlMap): void {
  const style = map.getStyle();
  for (const layer of style.layers ?? []) {
    try {
      if (layer.type === 'symbol' && /poi|shield/i.test(layer.id)) map.setLayoutProperty(layer.id, 'visibility', 'none');
      else if (layer.type === 'symbol') map.setPaintProperty(layer.id, 'text-color', '#3a3f45');
    } catch {
      /* skip */
    }
  }
}
